import { setForwardingTargetNumber } from "@kontextwerk/control-web-frame/shared/logic/telephony"
import { type SessionMode } from "../../../../_shared/_logic/enums"

declare const process: any

const API_BASE_STORAGE_KEY = "werk-admin:api-base-url"
const SESSION_MODE_STORAGE_KEY = "werk-admin:session-mode"

const SAMPLE_RATE = 24_000
const BUFFER_SIZE = 4_800

const isLocalHostname = (hostname: string): boolean =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname.endsWith(".localhost") ||
  hostname.endsWith(".local")

const sanitizeDomain = (value: string | undefined | null): string | null => {
  if (!value) return null

  const cleaned = value.trim()

  if (!cleaned) return null

  const withoutScheme = cleaned.replace(/^[a-z]+:\/\//i, "").replace(/^\/\//, "")
  const hostOnly = withoutScheme.split("/")[0]

  return hostOnly || null
}

const rawFallbackDomain =
  (typeof process !== "undefined"
    ? process.env?.CONTROL_DOMAIN || process.env?.DOMAIN || process.env?.HOST_DOMAIN
    : null) ?? null
const FALLBACK_DOMAIN = sanitizeDomain(rawFallbackDomain) ?? "kontextwerk.org"
const FALLBACK_CONTROL_HOST = FALLBACK_DOMAIN.startsWith("control-server.")
  ? FALLBACK_DOMAIN
  : `control-server.${FALLBACK_DOMAIN}`
const FALLBACK_HTTP_SCHEME = isLocalHostname(FALLBACK_DOMAIN) ? "http" : "https"
const FALLBACK_WS_SCHEME = FALLBACK_HTTP_SCHEME === "https" ? "wss" : "ws"

const FALLBACK_HTTP_BASE = `${FALLBACK_HTTP_SCHEME}://${FALLBACK_CONTROL_HOST}/api/v1`
const FALLBACK_WS_BASE = `${FALLBACK_WS_SCHEME}://${FALLBACK_CONTROL_HOST}/api/v1/voicebot/ws`

const deriveControlHost = (hostname: string, protocol: string): string => {
  // Simple shortcuts for bare localhost
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//control-server.localhost`
  }

  // Preserve already-correct control hosts
  if (hostname.startsWith("control-server.")) {
    return `${protocol}//${hostname}`
  }

  // For multi-stack dev hosts like app.kehrblatt-control.localhost
  if (hostname.includes(".")) {
    const baseDomain = hostname.split(".").slice(1).join(".") // drop first label

    if (baseDomain) {
      return `${protocol}//control-server.${baseDomain}`
    }
  }

  // Fallback to original single-stack default
  const normalizedProtocol = protocol.endsWith(":") ? protocol : `${protocol}:`

  if (normalizedProtocol.startsWith("http") && !isLocalHostname(hostname)) {
    return `${FALLBACK_HTTP_SCHEME}://${FALLBACK_CONTROL_HOST}`
  }

  return `${normalizedProtocol}//control-server.localhost`
}

const sanitizeBase = (value: string | null | undefined): string | null => {
  if (!value) return null

  const trimmed = value.trim()

  if (!trimmed) return null

  return trimmed.replace(/\/+$/, "")
}

const ensureApiSuffix = (base: string | null): string | null => {
  if (!base) return null

  const stripped = base.replace(/\/+$/, "")

  return stripped.endsWith("/api/v1") ? stripped : `${stripped}/api/v1`
}
const resolveDefaultBase = (): string => {
  if (typeof window !== "undefined") {
    const { protocol, hostname } = window.location

    return `${deriveControlHost(hostname, protocol)}/api/v1`
  }

  return FALLBACK_HTTP_BASE
}

const readStoredBase = (): string | null => {
  if (typeof window === "undefined") return null

  try {
    return sanitizeBase(window.localStorage?.getItem(API_BASE_STORAGE_KEY) ?? null)
  } catch (err) {
    console.warn("Failed to read stored API base URL", err)

    return null
  }
}

const computeWsFromBase = (base: string | null): string | null => {
  if (!base) return null

  try {
    const url = new URL(base.endsWith("/") ? base : `${base}/`)
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:"
    url.pathname = url.pathname.replace(/\/+$/, "") + "/voicebot/ws"
    url.search = ""
    url.hash = ""

    return url.toString().replace(/\/$/, "")
  } catch (err) {
    console.warn("Failed to derive WS URL from base", err)

    return null
  }
}

const enforceHttpsWhenPageSecure = (base: string | null): string | null => {
  if (!base) return null

  if (typeof window === "undefined") return base

  if (window.location.protocol !== "https:") return base

  try {
    const url = new URL(base)

    if (url.protocol === "http:") {
      url.protocol = "https:"

      return url.toString().replace(/\/$/, "")
    }

    return base
  } catch (err) {
    console.warn("Failed to enforce https for API base URL", err)

    return base
  }
}

let storedBase = sanitizeBase(readStoredBase())

if (typeof window !== "undefined" && storedBase) {
  try {
    const storedHost = new URL(storedBase).hostname
    const pageHost = window.location.hostname
    const pageIsLocal =
      pageHost === "localhost" || pageHost === "127.0.0.1" || pageHost.endsWith(".localhost")
    const storedIsStrictLocal = ["localhost", "127.0.0.1"].includes(storedHost)
    const storedIsLocalDomain = storedHost.endsWith(".localhost")

    // If we're on a *.localhost page, drop stored bases that point to service names / other domains.
    if (pageIsLocal && !(storedIsStrictLocal || storedIsLocalDomain)) {
      storedBase = null
    } else if (storedIsStrictLocal && (pageHost === "localhost" || pageHost === "127.0.0.1")) {
      storedBase = null
    } else if (pageIsLocal && storedIsLocalDomain) {
      const pageDomain = pageHost.split(".").slice(1).join(".")
      const storedDomain = storedHost.split(".").slice(1).join(".")

      if (pageDomain && storedDomain && pageDomain !== storedDomain) {
        storedBase = null
      }
    }
  } catch {
    storedBase = null
  }
}

const selectedBase = enforceHttpsWhenPageSecure(
  ensureApiSuffix(storedBase ?? sanitizeBase(resolveDefaultBase()) ?? FALLBACK_HTTP_BASE),
)
const baseAPIURL = selectedBase

const derivedWS =
  computeWsFromBase(baseAPIURL) ?? computeWsFromBase(resolveDefaultBase()) ?? FALLBACK_WS_BASE
const WS_URL = derivedWS

const sanitizeMode = (value: string | null | undefined): SessionMode | null => {
  if (!value) return null

  const normalized = value.trim().toLowerCase()

  if (normalized === "chat") return "chat"

  if (["voice", "call", "dev"].includes(normalized)) return "voice"

  return null
}

const readStoredMode = (): SessionMode | null => {
  if (typeof window === "undefined") return null

  try {
    const raw = window.localStorage?.getItem(SESSION_MODE_STORAGE_KEY) ?? null

    return sanitizeMode(raw)
  } catch (err) {
    console.warn("Failed to read stored session mode", err)

    return null
  }
}

const sessionMode: SessionMode = readStoredMode() ?? "voice"

const buildServiceHeaders = (): Record<string, string> => {
  return {}
}

const DEFAULT_FORWARDING_TARGET_NUMBER = "+4984195914300"

const resolveForwardingTargetNumber = (): string => {
  const configured =
    typeof process !== "undefined"
      ? process.env.CONTROL_PLATFORM_FORWARDING_PHONE_NUMBER?.trim()
      : null

  if (configured) return configured

  if (typeof window === "undefined") return DEFAULT_FORWARDING_TARGET_NUMBER

  const hostname = window.location.hostname.toLowerCase()

  if (hostname === "app.kerhblatt.dev" || hostname.endsWith(".kerhblatt.dev")) {
    return "+4984195914293"
  }

  if (hostname === "app.kehrblatt.de" || hostname.endsWith(".kehrblatt.de")) {
    return "+4915888638036"
  }

  if (
    hostname === "app.kehrblatt-control.kontextwerk.org" ||
    hostname.endsWith(".kehrblatt-control.kontextwerk.org")
  ) {
    return "+4984195914300"
  }

  return DEFAULT_FORWARDING_TARGET_NUMBER
}

const FORWARDING_TARGET_NUMBER = resolveForwardingTargetNumber()
setForwardingTargetNumber(FORWARDING_TARGET_NUMBER)
export {
  WS_URL,
  SAMPLE_RATE,
  BUFFER_SIZE,
  baseAPIURL,
  API_BASE_STORAGE_KEY,
  SESSION_MODE_STORAGE_KEY,
  sessionMode,
  buildServiceHeaders,
  FORWARDING_TARGET_NUMBER,
}
